import React, { useMemo, useCallback, useEffect } from "react";
import { MentionsInput, Mention } from "react-mentions";
import { FormControl, FormLabel, Typography } from "@mui/material";
import { DataService } from "../../services/DataService";
import debounce from "lodash/debounce";

export const MentionInput = ({
  value,
  onChange,
  label,
  placeholder,
  error,
  helperText,
  ignoreNonAlphanumeric = true,
  endpoint,
  queryParam = "name",
  orderParam = "name",
  itemsPerPage = 10,
  idField = "id",
  displayField = "name",
  extraFields = [],
  trigger = "@",
  markupTemplate = "[id='__id__' title='__display__']",
  entityType = "item",
  renderSuggestion,
  activity = null,
}) => {
  const dataService = useMemo(() => new DataService(), []);

  // Function to actually fetch mentions
  const doFetchMentions = useCallback(
    async (query, callback) => {
      if (!query) return callback([]);

      if (ignoreNonAlphanumeric && !/^[a-zA-Z0-9]+$/.test(query)) {
        return callback([]);
      }

      try {
        const queryParams = {
          pagination: false,
          [`order[${orderParam}]`]: "ASC",
          itemsPerPage,
          activity: activity,
        };
        queryParams[queryParam] = query;

        const res = await dataService.doRequest("GET", endpoint, queryParams);

        const suggestions =
          res.member?.map((item) => {
            const suggestion = {
              id: item[idField],
              display: item[displayField],
            };

            // Add any extra fields to the suggestion object
            extraFields.forEach((field) => {
              if (item[field]) suggestion[field] = item[field];
            });

            return suggestion;
          }) || [];

        callback(suggestions);
      } catch (err) {
        console.error(`Error fetching data from ${endpoint}:`, err);
        callback([]);
      }
    },
    [
      dataService,
      ignoreNonAlphanumeric,
      endpoint,
      queryParam,
      orderParam,
      idField,
      displayField,
      extraFields,
      itemsPerPage,
    ],
  );

  // Wrap the API call with debounce
  const fetchMentions = useMemo(
    () => debounce(doFetchMentions, 300),
    [doFetchMentions],
  );

  const handleKeyDown = useCallback((e) => {
    /**
     * Prevent Ctrl+X (cut operation) on Windows/Linux and Command+X on macOS.
     * Without this trick, when we cut, the cursor jumps to the end of the input.
     * This is a known issue in react-mentions unfortunately.
     */
    if ((e.ctrlKey || e.metaKey) && e.key === "x") {
      e.preventDefault();
    }
  }, []);

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      fetchMentions.cancel();
    };
  }, [fetchMentions]);

  // Default suggestion renderer
  const defaultRenderSuggestion = (suggestion, search, highlightedDisplay) => (
    <div>
      <strong>{suggestion.display}</strong>
      {suggestion.accountNumber && ` (${suggestion.accountNumber})`}
    </div>
  );

  return (
    <FormControl fullWidth error={error}>
      {label && <FormLabel>{label}</FormLabel>}
      {helperText && (
        <Typography
          variant=""
          sx={{
            color: "text.secondary",
            fontSize: "0.65rem",
            fontWeight: "bold",
            fontStyle: "italic",
          }}
        >
          {helperText}
        </Typography>
      )}
      <MentionsInput
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          placeholder ||
          `Enter something (use ${trigger} to reference ${entityType}s)`
        }
        style={{
          control: {
            backgroundColor: "#fff",
            fontSize: 14,
            lineHeight: "16px",
            minHeight: 100,
          },
          input: {
            margin: 0,
            padding: "8px 14px",
            border: error ? "1px solid #d32f2f" : "1px solid #c4c4c4",
            borderRadius: 4,
          },
          suggestions: {
            list: {
              backgroundColor: "white",
              border: "1px solid rgba(0,0,0,0.15)",
              fontSize: 14,
            },
            item: {
              padding: "5px 15px",
              borderBottom: "1px solid rgba(0,0,0,0.15)",
              "&focused": {
                backgroundColor: "#e3f2fd",
              },
            },
          },
          highlighter: {
            padding: "8px 14px",
          },
          mentions: {
            backgroundColor: "#e3f2fd",
            padding: "3px 4px",
            borderRadius: "4px",
            margin: "0 2px",
          },
        }}
      >
        <Mention
          trigger={trigger}
          data={fetchMentions}
          displayTransform={(id, display) => `${trigger}${display}`}
          markup={markupTemplate}
          appendSpaceOnAdd
          style={{ backgroundColor: "#d1c4e9" }}
          renderSuggestion={renderSuggestion || defaultRenderSuggestion}
        />
      </MentionsInput>
    </FormControl>
  );
};
